var _ = require('underscore');
var TorsoBehavior = require('backbone-torso/modules/Behavior');
var appointmentTypeLevelEnum = require('./../appointment-type/appointmentTypeLevelEnum');

/**
 * @method _isServiceLevelModel
 * @param appointmentTypeModel {AppointmentTypeModel} the model to check if it is service-level.
 * @return {boolean} true if it is service level, false or undefined if it is not service level.
 * @private
 */
function _isServiceLevelModel(appointmentTypeModel) {
  return appointmentTypeModel && appointmentTypeModel.get('level') === appointmentTypeLevelEnum.SERVICE;
}

/**
 * @method _filterServiceLevelModels
 * @param appointmentTypeModels {AppointmentTypeModel[]} the models to check if they are service-level.
 * @return {AppointmentTypeModel[]} the models that are service level.
 * @private
 */
function _filterServiceLevelModels(appointmentTypeModels) {
  return _.filter(appointmentTypeModels, _isServiceLevelModel);
}

/**
 * This behavior adds the listeners to set parent and grandparent types for appointment types
 *
 * @class AppointmentTypeParentBehavior
 * @constructor
 */
module.exports = TorsoBehavior.extend({
  _apptBehaviorName: 'appointmentType',
  _parentApptBehaviorName: 'parentAppointmentType',
  _grandparentApptBehaviorName: 'grandparentAppointmentType',
  _renderOnParentFetched: true,

  /**
   * @method initialize
   * @param attributes {Object}
   * @param options {Object}
   *   @param [options.apptBehaviorName}] {String} the name of the appointment type external id data behavior used in the view
   *   @param [options.parentApptBehaviorName] {String} the name of the appointment type vecna id data behavior used for parents in the view
   *   @param [options.grandparentApptBehaviorName] {String} the name of the appointment type vecna id data behavior used for grandparents in the view
   *   @param [options.renderOnParentFetched] {Boolean} whether to trigger the view to render when a parent is set on an appointment type
   */
  initialize: function(attributes, options) {
    _.bindAll(this, '_setParent', '_setGrandparent');

    options = options || {};
    this._apptBehaviorName = options.apptBehaviorName || this._apptBehaviorName;
    this._parentApptBehaviorName = options.parentApptBehaviorName || this._parentApptBehaviorName;
    this._grandparentApptBehaviorName = options.grandparentApptBehaviorName || this._grandparentApptBehaviorName;
    this._renderOnParentFetched = options.hasOwnProperty('renderOnParentFetched') ? options.renderOnParentFetched : this._renderOnParentFetched;
    this.on('change:parent change:grandparent', this._conditionalRender);
  },

  mixin: {
    /**
     * @method getServiceLevelAppointmentType
     * @param appointmentTypeId {String} the identifier for the appt type.
     * @return {AppointmentTypeModel} the service level for the given appt type id.  Could be the parent of the identified model.
     */
    getServiceLevelAppointmentType: function(appointmentTypeId) {
      if (!appointmentTypeId) {
        return null;
      }
      var appointmentTypeModel = this._getAppointmentTypeBehavior().data.getModel(appointmentTypeId);
      if (!appointmentTypeModel) {
        return null;
      }
      if (_isServiceLevelModel(appointmentTypeModel)) {
        return appointmentTypeModel;
      }
      var parentAppointmentTypeModel = appointmentTypeModel.getParent();
      if (_isServiceLevelModel(parentAppointmentTypeModel)) {
        return parentAppointmentTypeModel;
      }
      return null;
    },

    /**
     * @method getServiceLevelAppointmentTypes
     * @return {AppointmentTypeModel[]} an array of all torso models that represent the service level appt types based on the current
     *                                  retrieved appointment types (base level).
     */
    getServiceLevelAppointmentTypes: function() {
      var serviceLevelAppointmentTypeModels = _filterServiceLevelModels(this._getAppointmentTypeModels());
      var serviceLevelParentAppointmentTypeModels = _filterServiceLevelModels(this._getParentAppointmentTypeModels());
      // the grandparents are never service-level since the entire hierarchy is 3 levels and service is the middle level.
      return serviceLevelAppointmentTypeModels.concat(serviceLevelParentAppointmentTypeModels);
    },

    /**
     * @method getAppointmentTypeBehavior
     * @return {AppointmentTypeDataBehavior} the leaf appointment type behavior from the view.
     */
    getAppointmentTypeBehavior: function() {
      return this._getAppointmentTypeBehavior();
    },

    /**
     * @method getAppointmentTypeModels
     * @return {AppointmentTypeModel[]} all appointment type models that have been fetched for this view.
     */
    getAppointmentTypeModels: function() {
      return this._getAppointmentTypeModels();
    },

    /**
     * @method getParentAppointmentTypeBehavior
     * @return {AppointmentTypeDataBehavior} the parent appointment type behavior from the view.
     */
    getParentAppointmentTypeBehavior: function() {
      return this._getParentAppointmentTypeBehavior();
    },

    /**
     * @method getParentAppointmentTypeModels
     * @return {AppointmentTypeModel[]} all parent appointment type models that have been fetched for this view.
     */
    getParentAppointmentTypeModels: function() {
      return this._getParentAppointmentTypeModels();
    },

    /**
     * @method _getGrandparentAppointmentTypeBehavior
     * @return {AppointmentTypeDataBehavior} the grandparent appointment type behavior from the view.
     */
    getGrandparentAppointmentTypeBehavior: function() {
      return this._getGrandparentAppointmentTypeBehavior();
    },

    /**
     * @method getGrandparentAppointmentTypeModels
     * @return {AppointmentTypeModel[]} all grandparent appointment type models that have been fetched for this view.
     */
    getGrandparentAppointmentTypeModels: function() {
      return this._getGrandparentAppointmentTypeModels();
    }
  },

  /**
   * @method _attached
   * @override
   */
  _attached: function() {
    var parentAppointmentTypeBehavior = this._getParentAppointmentTypeBehavior();
    if (parentAppointmentTypeBehavior) {
      this.listenTo(parentAppointmentTypeBehavior, 'fetched', this._setParents);
      this._setParents();
    }
    var grandparentAppointmentTypeBehavior = this._getGrandparentAppointmentTypeBehavior();
    if (grandparentAppointmentTypeBehavior) {
      this.listenTo(grandparentAppointmentTypeBehavior, 'fetched', this._setGrandparents);
      this._setGrandparents();
    }
  },

  /**
   * @method _detached
   * @override
   */
  _detached: function() {
    var parentAppointmentTypeBehavior = this._getParentAppointmentTypeBehavior();
    if (parentAppointmentTypeBehavior) {
      this.stopListening(parentAppointmentTypeBehavior, 'fetched');
    }

    var grandparentAppointmentTypeBehavior = this._getGrandparentAppointmentTypeBehavior();
    if (grandparentAppointmentTypeBehavior) {
      this.stopListening(grandparentAppointmentTypeBehavior, 'fetched');
    }
  },

  /**
   * Sets all parents based on the leaf appointment type models.
   * @method _setGrandparents
   * @private
   */
  _setParents: function() {
    _.each(this._getAppointmentTypeModels(), this._setParent);
  },

  /**
   * Sets the parent on the appointment type; if the parent appointment type has a parent skips a potential rerender
   * @method _setParent
   * @param apptType {AppointmentTypeModel} the appointment type model to use to identify the parent.
   */
  _setParent: function(apptType) {
    var parentAppointmentTypeBehavior = this._getParentAppointmentTypeBehavior();
    if (!apptType || !parentAppointmentTypeBehavior) {
      return;
    }

    var parentExternalId = apptType.get('parentExternalId');
    if (!parentExternalId) {
      return;
    }
    var parentType = parentAppointmentTypeBehavior.data.getModel(parentExternalId);
    if (parentType) {
      apptType.setParent(parentType);
      var appointmentTypeBehavior = this._getAppointmentTypeBehavior();
      if (appointmentTypeBehavior) {
        appointmentTypeBehavior.trigger('change:parent');
      }
      this.trigger('change:parent');
    }
  },

  /**
   * Sets all grandparents based on parent models.
   * @method _setGrandparents
   * @private
   */
  _setGrandparents: function() {
    _.each(this._getParentAppointmentTypeModels(), this._setGrandparent);
  },

  /**
   * Sets the parent on the parent appointment type and re-renders
   * @method _setGrandparent
   * @param parentType {AppointmentTypeModel} the parent type model to use to identify the grandparent.
   */
  _setGrandparent: function(parentType) {
    var grandparentAppointmentTypeBehavior = this._getGrandparentAppointmentTypeBehavior();
    if (!parentType || !grandparentAppointmentTypeBehavior) {
      return;
    }

    var grandparentExternalId = parentType.get('parentExternalId');
    if (!grandparentExternalId) {
      return;
    }
    var grandparentType = grandparentAppointmentTypeBehavior.data.getModel(grandparentExternalId);
    if (grandparentType) {
      parentType.setParent(grandparentType);
      var parentAppointmentTypeBehavior = this._getParentAppointmentTypeBehavior();
      if (parentAppointmentTypeBehavior) {
        parentAppointmentTypeBehavior.trigger('change:parent');
      }
      this.trigger('change:grandparent');
    }
  },

  /**
   * Renders the view if the behavior is meant to do so
   * @method _conditionalRender
   */
  _conditionalRender: function() {
    if (this._renderOnParentFetched) {
      this.view.render();
    }
  },

  /**
   * @method _getAppointmentTypeBehavior
   * @return {AppointmentTypeDataBehavior} the leaf appointment type behavior from the view.
   * @private
   */
  _getAppointmentTypeBehavior: function() {
    return this.view.getBehavior(this._apptBehaviorName);
  },

  /**
   * @method _getAppointmentTypeModels
   * @return {AppointmentTypeModel[]} all appointment type models that have been fetched for this view.
   * @private
   */
  _getAppointmentTypeModels: function() {
    var appointmentTypeBehavior = this._getAppointmentTypeBehavior();
    return appointmentTypeBehavior ? appointmentTypeBehavior.data.getModels() : [];
  },

  /**
   * @method _getParentAppointmentTypeBehavior
   * @return {AppointmentTypeDataBehavior} the parent appointment type behavior from the view.
   * @private
   */
  _getParentAppointmentTypeBehavior: function() {
    return this.view.getBehavior(this._parentApptBehaviorName);
  },

  /**
   * @method _getParentAppointmentTypeModels
   * @return {AppointmentTypeModel[]} all parent appointment type models that have been fetched for this view.
   * @private
   */
  _getParentAppointmentTypeModels: function() {
    var parentAppointmentTypeBehavior = this._getParentAppointmentTypeBehavior();
    return parentAppointmentTypeBehavior ? parentAppointmentTypeBehavior.data.getModels() : [];
  },

  /**
   * @method _getGrandparentAppointmentTypeBehavior
   * @return {AppointmentTypeDataBehavior} the grandparent appointment type behavior from the view.
   * @private
   */
  _getGrandparentAppointmentTypeBehavior: function() {
    return this.view.getBehavior(this._grandparentApptBehaviorName);
  },

  /**
   * @method _getGrandparentAppointmentTypeModels
   * @return {AppointmentTypeModel[]} all grandparent appointment type models that have been fetched for this view.
   * @private
   */
  _getGrandparentAppointmentTypeModels: function() {
    var grandparentAppointmentTypeBehavior = this._getGrandparentAppointmentTypeBehavior();
    return grandparentAppointmentTypeBehavior ? grandparentAppointmentTypeBehavior.data.getModels() : [];
  }
});
